import React, { useCallback, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import { sizePropTypes } from '@ecp/common/src/style/propTypes';
import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import InputUnstyled from '@ecp/common/src/components/unstyled/input/InputUnstyled';

import { StyledErrorMessage, StyledInputRoot } from './Input';

const StyledCount = styled(FlexBox)`
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.sub};
  line-height: 160%;
  color: ${boTheme.color.sub2};
`;

const Count = ({ value, maxLength }) => {
  const count = value?.length || 0;

  return (
    <StyledCount width="max-content" align-items="flex-end">
      <TextBox color={boTheme.color.sub1}>{count}</TextBox>/{maxLength}
    </StyledCount>
  );
};

Count.propTypes = {
  value: PropTypes.string,
  maxLength: PropTypes.number,
};

const StyledTextareaRoot = styled(StyledInputRoot)`
  padding: 7px 5px 7px 7px;
  flex-direction: column;
`;

const StyledTextarea = styled.textarea`
  padding: 1px;
  border: none;
  outline: none;
  resize: none;
  width: 100%;
  height: 100%;
  background-color: transparent;
  box-sizing: border-box;
  user-select: auto;

  &:focus::placeholder {
    color: transparent;
  }
`;

const Textarea = React.forwardRef(
  ({ countCheck, countCheckComponent: CountCheckComponent, onEnter, error, ...props }, ref) => {
    const { value, endAdornment, maxLength, errorMessage } = props;

    const textareaRef = useRef(null);
    useImperativeHandle(ref, () => textareaRef.current);

    const { ignoreError } = useIgnorableError({ error, value });

    const handleEnter = useCallback(
      (e) => {
        e.stopPropagation();
        onEnter?.(e);
      },
      [onEnter]
    );

    return (
      <FlexBoxColumn width={props.width} align-items="flex-start">
        <InputUnstyled
          rootComponent={StyledTextareaRoot}
          rootProps={{ width: props.width, height: props.height }}
          inputComponent={StyledTextarea}
          error={!ignoreError && error}
          ref={textareaRef}
          endAdornment={
            <FlexBox gap="10px" width="100%" justify-content="flex-end" flex="0">
              {countCheck && <CountCheckComponent value={value} maxLength={maxLength} />}
              {endAdornment}
            </FlexBox>
          }
          onEnter={handleEnter}
          {...props}
        />
        {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      </FlexBoxColumn>
    );
  }
);

Textarea.displayName = 'Textarea';

Textarea.propTypes = {
  ...sizePropTypes,
};
Textarea.defaultProps = {
  placeholder: '내용을 입력하세요.',
  width: '260px',
  height: '106px',
  countCheckComponent: Count,
  onFocus: () => {},
};

export default Textarea;
